import {round, floor, padStart} from 'lodash'

export const MILLISECONDS_IN_SEC = 1000
export const SECONDS_IN_MINUTE = 60
export const MINUTES_IN_HOUR = 60
export const HOURS_IN_DAYS = 24
export const MILLISECONDS_IN_MINUTE = MILLISECONDS_IN_SEC * SECONDS_IN_MINUTE
export const MILLISECONDS_IN_HOUR = MILLISECONDS_IN_MINUTE * MINUTES_IN_HOUR
export const MILLISECONDS_IN_DAY = MILLISECONDS_IN_HOUR * HOURS_IN_DAYS
export const MONTHS_IN_YEAR = 12
export const QUARTER_IN_SEC = 0.25

export function getMillisecondsFromSecond(second: number): number {
  return second * MILLISECONDS_IN_SEC
}

export function getMillisecondsFromMinutes(minute: number): number {
  return minute * MILLISECONDS_IN_MINUTE
}

export function getSecondsFromMilliseconds(milliseconds: number): number {
  return Math.floor(milliseconds / MILLISECONDS_IN_SEC)
}

export function getMinutesFromMilliseconds(milliseconds: number): number {
  return Math.floor(milliseconds / MILLISECONDS_IN_MINUTE)
}

export function getHoursFromMilliseconds(milliseconds: number): number {
  return Math.floor(milliseconds / MILLISECONDS_IN_HOUR)
}

export function getDaysFromMilliseconds(milliseconds: number, fullDays: boolean = true): number {
  return fullDays ? Math.floor(milliseconds / MILLISECONDS_IN_DAY) : Math.ceil(milliseconds / MILLISECONDS_IN_DAY)
}

export function getNeatMinutesFromMilliseconds(milliseconds: number): number {
  return getMinutesFromMilliseconds(milliseconds - (getHoursFromMilliseconds(milliseconds) * MILLISECONDS_IN_HOUR))
}

export function getNeatHoursFromMilliseconds(milliseconds: number): number {
  return getHoursFromMilliseconds(milliseconds - (getDaysFromMilliseconds(milliseconds) * MILLISECONDS_IN_DAY))
}

export function getDatesDifference(dateFirst: Date, dateSecond: Date): number {
  return dateFirst.getTime() - dateSecond.getTime()
}

export function getDatesDifferenceByNow(date: Date): number {
  const now = new Date()
  const timeZoneOffsetSplit = (now.getTimezoneOffset() - date.getTimezoneOffset()) * MILLISECONDS_IN_MINUTE
  return date.getTime() - now.getTime() + timeZoneOffsetSplit
}

export function increasedNowBySeconds(seconds: number): Date {
  const result = new Date()
  if (seconds > 0) {
    result.setSeconds(result.getSeconds() + seconds)
  }
  return result
}

export const getUTCTime = (): number => {
  const localDate = new Date()

  return new Date(
    localDate.getUTCFullYear(),
    localDate.getUTCMonth(),
    localDate.getUTCDate(),
    localDate.getUTCHours(),
    localDate.getUTCMinutes(),
    localDate.getUTCSeconds(),
    localDate.getUTCMilliseconds()
  ).getTime()
}

export function formatSeconds(totalSeconds: number): number | string {
  if (totalSeconds < SECONDS_IN_MINUTE) {
    return totalSeconds
  }

  const roundedSeconds = round(totalSeconds)
  const minutes = floor(roundedSeconds / SECONDS_IN_MINUTE)
  const seconds = roundedSeconds - (minutes * SECONDS_IN_MINUTE)
  const precedingZero = seconds >= 10 ? '' : 0

  return `${minutes}:${precedingZero}${seconds}`
}

// tslint:disable-next-line:no-bitwise
export const getWholeMinutesFromSeconds = (seconds: number) => ~~(seconds / SECONDS_IN_MINUTE)

export const getRemainderSeconds = (seconds: number) => parseInt(String(seconds % SECONDS_IN_MINUTE), 10)

export function getQuarterSeconds(seconds: number): number {
  const delta = seconds ? seconds % 1 : 0
  if (delta >= QUARTER_IN_SEC) {
    return Math.min(Math.round(delta * 4) * 25, 75)
  }
  return 0
}

export const getRestMillisecondsFromSeconds = (time: number): number => Math.floor((time % 1) * Math.pow(10, 3))

export const getMinutesAndSecondsAndMilliseconds = (time: number) => ({
  minutes: getWholeMinutesFromSeconds(time),
  seconds: getRemainderSeconds(time),
  milliseconds: getRestMillisecondsFromSeconds(time)
})

export const getFullSecondsFromMinutesAndSecondsAndMilliseconds =
  (minutes: number, seconds: number, milliseconds: number): number => {
    const fixMilliseconds = milliseconds / MILLISECONDS_IN_SEC
    const fixSeconds = Math.min(seconds, SECONDS_IN_MINUTE - 1)
    const fixMinutes = minutes * SECONDS_IN_MINUTE
    return fixMinutes + fixSeconds + fixMilliseconds
  }

export function formatFractionalSeconds(totalSeconds: number): string {
  const quarterSeconds = getQuarterSeconds(totalSeconds)
  return quarterSeconds ? `:${quarterSeconds}` : ':00'
}

export function formatFractionalHalfSeconds(totalSeconds: number): string {
  const delta = totalSeconds ? totalSeconds % 1 : 0
  return (delta >= 0.5) ? ':5' : ':0'
}

export function formatFractionalQuarterSeconds(totalSeconds: number): string {
  const quarterSeconds = getQuarterSeconds(totalSeconds)
  return quarterSeconds ? `:${quarterSeconds}` : ':00'
}

export const formatDurationOutput = (
  totalSeconds: number,
  formatFractional: (value: number) => string = formatFractionalHalfSeconds
): string => {
  const deltaHalf = formatFractional(totalSeconds)
  const roundedSeconds = Math.trunc(totalSeconds)

  if (roundedSeconds < SECONDS_IN_MINUTE) {
    return `${roundedSeconds}${deltaHalf}`
  }

  const minutes = floor(roundedSeconds / SECONDS_IN_MINUTE)
  const seconds = roundedSeconds - (minutes * SECONDS_IN_MINUTE)
  const precedingZero = seconds >= 10 ? '' : 0

  return `${minutes}:${precedingZero}${seconds}${deltaHalf}`
}

export function timePadLeft(value: any, doubleZero = true): string {
  return padStart(value, 2, doubleZero ? '0' : '')
}

export function getFormattedTime(time: any, doubleZeroMinutes: boolean = true): string {
  const minutes = getWholeMinutesFromSeconds(time)
  const seconds = getRemainderSeconds(time)
  return `${timePadLeft(minutes, doubleZeroMinutes)}:${timePadLeft(seconds)}`
}

export function getTimeLimitLabel(time: any, doubleZeroMinutes: boolean = false): string {
  const minutes = getWholeMinutesFromSeconds(time)
  const seconds = getRemainderSeconds(time)
  if (seconds) {
    if (minutes) {
      return `${timePadLeft(minutes, doubleZeroMinutes)} minutes ${timePadLeft(seconds)} seconds`
    }
    return `${seconds} second${seconds !== 1 ? 's' : ''}`
  }
  return `${minutes} minute${minutes !== 1 ? 's' : ''}`
}

export const getHoursMinutesSecondsFromSeconds = (seconds: number) =>
  new Date(seconds * 1000).toISOString().slice(11, 19) // HH:MM:SS

export const formatCustomDate = (dateString: string) => {
  const date = new Date(dateString)
  const options: Intl.DateTimeFormatOptions =
      {month: 'short', day: '2-digit', hour: 'numeric', minute: '2-digit', hour12: true}
  const formatter = new Intl.DateTimeFormat('en-US', options)

  return formatter.format(date)
}
